import { onMounted, onUnmounted, ref } from 'vue'
// @ts-expect-error importing untyped JS module into TypeScript
import { logEvent } from '@/services/clients/firebase'
// @ts-expect-error import path to avoid circular dependency issues
import store from '@/store'

export const WALKTHROUGH_SCHEDULER_URL = 'https://scheduler.zoom.us/fareclock-support/console-walkthrough'

const ZOOM_SCHEDULER_ORIGIN = 'https://scheduler.zoom.us'

export interface WalkthroughPrefill {
  firstName?: string
  lastName?: string
  email?: string
}

/**
 * Splits a single display name into the first/last name Zoom's booking form prefill wants.
 * `userProfile.userName` (see getWalkthroughPrefillFromStore below) is one already-joined
 * display name -- there's no separate first/last name anywhere in the console's own data -- so
 * this is a heuristic, not a guarantee. Zoom's fields stay editable, so a wrong split just means
 * the person fixes it rather than typing it from scratch.
 */
export function splitDisplayName (fullName?: string | null): { firstName?: string, lastName?: string } {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return {}
  }
  const [firstName, ...rest] = parts
  return { firstName, lastName: rest.join(' ') || undefined }
}

/** Reads the signed-in user's name/email out of the store, for prefilling the scheduler form. */
export function getWalkthroughPrefillFromStore (): WalkthroughPrefill {
  const userProfile = store.state.userProfile ?? {}
  return {
    ...splitDisplayName(userProfile.userName),
    email: userProfile.emailAddress || undefined
  }
}

/**
 * Builds the embed URL for the Zoom scheduler, matching the embed on fareclock.com but with an
 * `origin` param added so Zoom posts a `bookingForm` message back to us on a completed booking
 * (see useWalkthroughBooking below). Do not hardcode a bare hostname here -- Zoom feeds this
 * value straight to postMessage as a target origin, and a bare hostname throws on every message.
 *
 * `prefill` fills in the booking form's name/email fields, saving a step for a person we already
 * know. `firstname`/`lastname` are Zoom's documented prefill params (see
 * https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0083594); `email` prefilling
 * the same way is not documented there but verified working live, so treat it as best-effort --
 * if Zoom stops honoring it, the field just goes back to blank rather than failing.
 */
export function getWalkthroughEmbedUrl (prefill?: WalkthroughPrefill): string {
  const params = new URLSearchParams({ embed: 'true', origin: window.location.origin })
  if (prefill?.firstName) {
    params.set('firstname', prefill.firstName)
  }
  if (prefill?.lastName) {
    params.set('lastname', prefill.lastName)
  }
  if (prefill?.email) {
    params.set('email', prefill.email)
  }
  return `${WALKTHROUGH_SCHEDULER_URL}?${params.toString()}`
}

/**
 * Listens for Zoom's scheduler confirming a booking was made in the embedded iframe, so callers
 * can show a confirmation state instead of asking the user to self-report whether they booked.
 *
 * This relies on an undocumented Zoom postMessage -- their embed-code generator UI describes it
 * only as sending "form-filled information ... via postMessage at the final confirmation stage".
 * It can stop firing if Zoom changes their scheduler; `booked` simply never flips to true in that
 * case. Callers must not gate the ability to proceed (e.g. "Switch to Classic") on `booked`.
 *
 * `context` is attached to the resulting analytics event so booking rates can be compared across
 * the places we offer this (e.g. the classic-console revert flow vs. the Help menu).
 */
export function useWalkthroughBooking (context: string) {
  const booked = ref(false)

  function onMessage (event: MessageEvent) {
    if (event.origin !== ZOOM_SCHEDULER_ORIGIN || booked.value) {
      return
    }
    const data = event.data
    if (data?.type === 'bookingForm' && data.payload?.scheduledEventId) {
      booked.value = true
      logEvent('walkthrough_scheduled', { context })
    }
  }

  onMounted(() => window.addEventListener('message', onMessage))
  onUnmounted(() => window.removeEventListener('message', onMessage))

  return { booked }
}
